import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import { graphqlClient } from "@/lib/graphql";
import type { Collection } from "@/types";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/hooks/useAuth";
import { ListingCard } from "@/components/listings/ListingCard";
import { SiblingsPhotoMap } from "@/components/listings/SiblingsPhotoMap";

const COLLECTION_QUERY = gql`
  query Collection($id: ID!) {
    collection(id: $id) {
      id
      name
      coverImageUrl
      listingGroupId
      listingsCount
      createdAt
      cancelled
      user {
        id
        displayName
        avatarUrl
        rating
        ratingsCount
        activeListingsCount
      }
      listings {
        id
        price
        condition
        conditionLabel
        status
        createdAt
        displayTitle
        displayImageUrl
        imageUrls
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
        siblingListings {
          id
          status
          gameTitle
          price
          boundingBox {
            yMin
            xMin
            yMax
            xMax
          }
        }
        shippingAvailable
        shippingCost
        location
        game {
          id
          title
          imageUrl
          thumbnailUrl
        }
        seller {
          id
          displayName
          avatarUrl
          rating
          ratingsCount
        }
      }
      sellerOtherListings {
        id
        price
        condition
        conditionLabel
        status
        displayTitle
        displayImageUrl
        imageUrls
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
        siblingListings {
          id
          status
          gameTitle
          price
          boundingBox {
            yMin
            xMin
            yMax
            xMax
          }
        }
        game {
          id
          title
          thumbnailUrl
        }
        seller {
          id
          displayName
        }
      }
      otherCollections {
        id
        name
        coverImageUrl
        listingsCount
      }
    }
  }
`;

export function CollectionPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["collection", id],
    queryFn: () =>
      graphqlClient.request<{ collection: Collection | null }>(
        COLLECTION_QUERY,
        { id },
      ),
    enabled: !!id,
  });

  const collection = data?.collection;
  const listings = collection?.listings ?? [];

  usePageTitle(collection?.name ?? t("collections.title"));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-800 border-t-transparent" />
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg text-gray-500">{t("collections.not_found")}</p>
        <Link
          to="/listings"
          className="mt-4 inline-block text-sm font-medium text-primary-800 hover:text-primary-700"
        >
          {t("collections.back_to_listings")}
        </Link>
      </div>
    );
  }

  if (collection.cancelled) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("collections.unavailable_title")}
        </h1>
        <p className="mt-3 text-gray-600">{t("collections.unavailable_description")}</p>
        <Link
          to="/listings"
          className="mt-6 inline-block text-sm font-medium text-primary-800 hover:text-primary-700"
        >
          {t("collections.back_to_listings")}
        </Link>
      </div>
    );
  }

  const isGroupCollection = !!collection.listingGroupId;
  const imageUrl = collection.coverImageUrl;
  const listingsWithBbox = listings.filter((l) => l.boundingBox);
  const isOwner = currentUser?.id === collection.user.id;
  const hasPhotoMap = isGroupCollection && !!imageUrl && listingsWithBbox.length > 0;

  // Bundle summary
  const activeListings = listings.filter((l) => l.status === "active");
  const soldListings = listings.filter((l) => l.status === "sold");
  const visibleCount = listings.filter((l) => l.status !== "cancelled").length;
  const totalValue = activeListings.reduce((sum, l) => sum + (l.price ?? 0), 0);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Action bar */}
      <ActionBar collection={collection} isOwner={isOwner} />

      {/* Hero */}
      {hasPhotoMap ? (
        <SiblingsPhotoMap imageUrl={imageUrl!} siblings={listings} />
      ) : imageUrl ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-100 shadow-sm">
          <img src={imageUrl} alt={collection.name} className="w-full" />
        </div>
      ) : (
        <div className="flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 py-20 text-sm text-gray-400">
          {t("collections.empty")}
        </div>
      )}

      {/* Bundle summary */}
      {visibleCount > 0 && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-gray-600">
          <span className="font-medium text-gray-900">
            {t("collections.items_count", { count: visibleCount })}
          </span>
          {activeListings.length > 0 && (
            <>
              <span className="text-gray-300">·</span>
              <span>
                {t("collections.available_count", { count: activeListings.length })}
              </span>
            </>
          )}
          {soldListings.length > 0 && (
            <>
              <span className="text-gray-300">·</span>
              <span className="text-gray-500">
                {t("collections.sold_count", { count: soldListings.length })}
              </span>
            </>
          )}
          {totalValue > 0 && (
            <>
              <span className="text-gray-300">·</span>
              <span className="font-semibold text-primary-800">
                {t("collections.total_value", { value: totalValue.toFixed(2) })}
              </span>
            </>
          )}
        </div>
      )}

      {/* Seller strip */}
      <SellerStrip
        user={collection.user}
        canMessage={!!currentUser && !isOwner}
        isOwner={isOwner}
      />

      {/* Other listings (hide if empty) */}
      {collection.sellerOtherListings && collection.sellerOtherListings.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-4 text-xl font-bold text-gray-900">
            {t("collections.seller_other_listings_title")}
          </h2>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {collection.sellerOtherListings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        </section>
      )}

      {/* Other collections (hide if empty) */}
      {collection.otherCollections && collection.otherCollections.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-4 text-xl font-bold text-gray-900">
            {t("collections.other_collections_title")}
          </h2>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {collection.otherCollections.map((c) => (
              <Link
                key={c.id}
                to={`/collections/${c.id}`}
                className="card overflow-hidden transition-shadow hover:shadow-lg"
              >
                <div className="aspect-video overflow-hidden bg-gray-100">
                  {c.coverImageUrl && (
                    <img
                      src={c.coverImageUrl}
                      alt={c.name}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="p-3">
                  <p className="truncate text-sm font-semibold text-gray-900">{c.name}</p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {t("collections.count", { count: c.listingsCount })}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

interface ActionBarProps {
  collection: Collection;
  isOwner: boolean;
}

function ActionBar({ collection, isOwner }: ActionBarProps) {
  const { t } = useTranslation();
  const [listCopied, setListCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = t("collections.share_text", { name: collection.name });

  const handleCopyList = useCallback(() => {
    const lines: string[] = [t("collections.copy_list_heading"), ""];
    for (const l of collection.listings) {
      if (l.status === "cancelled") continue;
      const price = l.price != null ? ` — ${l.price.toFixed(2)}€` : "";
      const suffix =
        l.status === "sold"
          ? ` (${t("collections.sold")})`
          : l.status === "reserved"
            ? ` (${t("collections.reserved")})`
            : "";
      lines.push(`• ${l.displayTitle}${price}${suffix}`);
    }
    lines.push("", shareUrl);
    navigator.clipboard.writeText(lines.join("\n"));
    setListCopied(true);
    setTimeout(() => setListCopied(false), 2500);
  }, [collection.listings, shareUrl, t]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  }, [shareUrl]);

  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;

  return (
    <div className="mb-4 flex items-center justify-between gap-2">
      <div className="flex items-center gap-1">
        <a
          href={fbUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("collections.share_on_facebook")}
          title={t("collections.share_on_facebook")}
          className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-[#1877F2]"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        </a>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("collections.share_on_whatsapp")}
          title={t("collections.share_on_whatsapp")}
          className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-[#25D366]"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
          </svg>
        </a>
        <button
          onClick={handleCopyLink}
          aria-label={t("collections.share_copy_link")}
          title={linkCopied ? t("collections.link_copied") : t("collections.share_copy_link")}
          className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-gray-100 ${
            linkCopied ? "text-green-600" : "text-gray-500 hover:text-gray-900"
          }`}
        >
          {linkCopied ? (
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          )}
        </button>
      </div>

      {isOwner && (
        <button
          onClick={handleCopyList}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          title={t("collections.copy_list_hint")}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
          </svg>
          {listCopied ? t("collections.list_copied") : t("collections.copy_list")}
        </button>
      )}
    </div>
  );
}

interface SellerStripProps {
  user: Collection["user"];
  canMessage: boolean;
  isOwner: boolean;
}

function SellerStrip({ user, canMessage, isOwner }: SellerStripProps) {
  const { t } = useTranslation();

  return (
    <section className="mt-8 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-center gap-4">
        <Link
          to={`/users/${user.id}`}
          className="flex min-w-0 flex-1 items-center gap-3"
        >
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.displayName}
              className="h-12 w-12 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-800 text-lg font-semibold text-white">
              {user.displayName[0].toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-semibold text-gray-900">
              {user.displayName}
            </p>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-gray-500">
              {user.rating != null && user.ratingsCount > 0 && (
                <span className="inline-flex items-center gap-1">
                  <svg className="h-4 w-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  {user.rating.toFixed(1)} ({user.ratingsCount})
                </span>
              )}
              <span>
                {t("collections.active_listings_count", { count: user.activeListingsCount })}
              </span>
            </div>
          </div>
        </Link>
        {canMessage && (
          <Link
            to={`/messages?seller=${user.id}`}
            className="btn-primary shrink-0 text-sm"
          >
            {t("collections.send_message")}
          </Link>
        )}
        {isOwner && (
          <Link
            to={`/users/${user.id}`}
            className="btn-secondary shrink-0 text-sm"
          >
            {t("collections.view_profile")}
          </Link>
        )}
      </div>
    </section>
  );
}
